import { Box, Button, HStack, Link, Text } from '@chakra-ui/react';
import { WarningTriangle } from 'iconoir-react';
import { useTranslation } from 'react-i18next';
import { useCurrentBrandQuery } from '@/hooks/queries/use-current-brand-query';

/**
 * Whether the server says this brand cannot yet be named on a contract.
 *
 * `=== false` and never `!`: the flag is optional on the wire, and an absent
 * answer is not a "no". Reading undefined as incomplete would warn — and, at
 * the send, block — every brand the day this dashboard ships ahead of the API.
 *
 * One rule, one reading. The predicate that decides this lives on the server
 * and its answer is served; re-deriving it here from the company details would
 * give the brand a second opinion that can disagree with the send guard.
 */
export const useBrandLegalIdentityIncomplete = (): boolean => {
  const { data: brand } = useCurrentBrandQuery();
  return brand?.legalIdentityComplete === false;
};

interface CollabLegalIdentityBannerProps {
  /**
   * `early` warns at the entrance of the tunnel, where nothing is blocked yet
   * and the brand can fix it while writing the brief. `blocking` states the
   * refusal at the last screen, beside the Continue it disables.
   *
   * Same condition, same link, two moments: a brand told early never meets the
   * late one.
   */
  variant: 'early' | 'blocking';
}

const TONE = {
  early: { border: 'warning.border', bg: 'warning.surface', fg: 'warning.text' },
  blocking: { border: 'red.200', bg: 'red.50', fg: 'fg.error' },
} as const;

/**
 * The brand's own legal identity is missing, so a collab with a counterpart
 * cannot be sent: an accepted brief is a contract and the brand has to be
 * identifiable on it.
 *
 * The caller owns *whether* to show this — the entrance shows it on the bare
 * condition, the send step adds its counterpart test — and this owns what it
 * says and where it sends the brand.
 */
export const CollabLegalIdentityBanner = ({ variant }: CollabLegalIdentityBannerProps) => {
  const { t } = useTranslation();
  // Same query, same cache entry as the hook above: no second request.
  const { refetch, isFetching } = useCurrentBrandQuery();
  const tone = TONE[variant];
  const key = (name: string) => `invitations.modal.collab.wizard.send.brandIdentity.${name}`;

  return (
    <HStack
      gap="2.5"
      align="flex-start"
      borderWidth="1px"
      borderColor={tone.border}
      bg={tone.bg}
      rounded="lg"
      px="14px"
      py="10px"
    >
      <Text color={tone.fg} flex="none" mt="0.5">
        <WarningTriangle width={14} height={14} />
      </Text>
      <Box>
        <Text fontSize="12.5px" fontWeight="semibold" color={tone.fg} lineHeight="1.4">
          {t(key(variant === 'early' ? 'earlyTitle' : 'title'))}
        </Text>
        <Text fontSize="11.5px" color={tone.fg} lineHeight="1.5">
          {t(key(variant === 'early' ? 'earlyBody' : 'body'))}
        </Text>
        <HStack gap="3" mt="1.5" wrap="wrap">
          {/* A new tab, never the settings form mounted here: it navigates on
              success, which would destroy the draft in progress. On return the
              brand query refetches on window focus and the banner clears
              itself; the recheck is the manual fallback. */}
          <Link
            href="/settings/general"
            target="_blank"
            rel="noopener noreferrer"
            fontSize="11.5px"
            fontWeight="medium"
            color={tone.fg}
            textDecoration="underline"
          >
            {t(key('cta'))}
          </Link>
          {/* A real button, not a Text or an href-less Link: the fallback has
              to be reachable from the keyboard like the one beside it. */}
          <Button
            variant="ghost"
            size="sm"
            h="auto"
            minW="0"
            px="0"
            py="0"
            fontSize="11.5px"
            fontWeight="medium"
            color={tone.fg}
            textDecoration="underline"
            _hover={{ bg: 'transparent', color: tone.fg }}
            loading={isFetching}
            onClick={() => void refetch()}
          >
            {t(key('recheck'))}
          </Button>
        </HStack>
      </Box>
    </HStack>
  );
};
